/**
 * Request-time context-window pruning for the in-browser Chat agent
 * (issue #2). Mirror of backend/src/llm/context.ts — keep the two in sync
 * when changing the algorithm.
 *
 * Runs in the agent's `transformContext` hook, i.e. it only trims what is
 * sent to the model per request. The session stored in IndexedDB keeps the
 * full message history untouched.
 */

interface AnyMessage {
	role?: string;
	[k: string]: unknown;
}

/** Conservative chars-per-token estimate — overestimates token counts so we
 *  prune early rather than risk vLLM's hard 400 at the context limit. */
const CHARS_PER_TOKEN = 3;

/** Reserved for system prompt, tool schemas and chat-template overhead,
 *  none of which appear in `messages`. */
const RESERVE_TOKENS = 8_192;

function estimateMessageTokens(m: unknown): number {
	try {
		return Math.ceil(JSON.stringify(m).length / CHARS_PER_TOKEN);
	} catch {
		return 0;
	}
}

/**
 * Keep the newest messages that fit into
 * `contextWindow - maxOutputTokens - RESERVE_TOKENS`, aligned so the window
 * never starts with an orphaned toolResult/assistant message. Never throws
 * (pi-agent-core contract) — falls back to the original list.
 */
export function pruneContextMessages<T>(
	messages: T[],
	contextWindow: number,
	maxOutputTokens: number,
): T[] {
	try {
		const budget = contextWindow - maxOutputTokens - RESERVE_TOKENS;
		if (budget <= 0 || messages.length <= 1) return messages;

		const sizes = messages.map(estimateMessageTokens);
		const total = sizes.reduce((a, b) => a + b, 0);
		if (total <= budget) return messages;

		let used = 0;
		let start = messages.length;
		for (let i = messages.length - 1; i >= 0; i--) {
			const size = sizes[i] ?? 0;
			if (used + size > budget && start < messages.length) break;
			used += size;
			start = i;
			if (used > budget) break;
		}

		let aligned = start;
		while (aligned < messages.length - 1) {
			const role = (messages[aligned] as AnyMessage).role ?? "";
			if (role === "user" || role === "user-with-attachments") break;
			aligned++;
		}
		const cut = aligned < messages.length - 1 ? aligned : start;
		if (cut <= 0) return messages;

		console.info(`[chat] context pruned: dropping ${cut} oldest message(s) for this request`);
		const kept = messages.slice(cut);
		const note = {
			role: "user",
			content:
				`[Hinweis: Diese Unterhaltung ist länger als das Kontextfenster des Modells. ` +
				`Die ältesten ${cut} Nachrichten wurden für diese Anfrage ausgeblendet; ` +
				`sie bleiben im Verlauf gespeichert.]`,
			timestamp: Date.now(),
		} as unknown as T;
		return [note, ...kept];
	} catch {
		return messages;
	}
}
